import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {CustomField} from 'constants/MolbioConstants';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {BLUE, GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface AddCustomFieldMenuProps {
  allCustomFieldKeyOptions: Array<string>;
  onAddField: (key: string) => void;
}

function AddCustomFieldMenu({onAddField, allCustomFieldKeyOptions}: AddCustomFieldMenuProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [newFieldKey, setNewFieldKey] = useState<string>('');
  const fieldKeyOptionDatas = allCustomFieldKeyOptions.map((fieldKey: string) => ({
    name: fieldKey,
    value: fieldKey,
  }));

  function getMatchingOptionDatas(query: string, optionDatas: Array<OptionData>): Array<OptionData> {
    const trimmedQuery = query.trim();
    setNewFieldKey(trimmedQuery);
    if (trimmedQuery.length === 0) {
      return fieldKeyOptionDatas;
    } else if (/\s/.test(trimmedQuery) || allCustomFieldKeyOptions.includes(trimmedQuery)) {
      // Custom field keys cannot contain whitespace, so do not show the option to create a new key
      return optionDatas.filter(({value}) => value.includes(trimmedQuery));
    } else {
      return [
        ...optionDatas.filter(({value}) => value.includes(trimmedQuery)),
        {
          name: <p className={css(styles.createNewOption)}>{`Create new '${trimmedQuery}' field`}</p>,
          value: '',
        },
      ];
    }
  }

  // On selection, add a new field with the selected key. If the selected key value is an empty string,
  // add a new key of the current search value.
  function handleSelectKey(selectedKey: string): void {
    onAddField(selectedKey.length === 0 ? newFieldKey : selectedKey);
  }

  return (
    <Dropdown
      alwaysDropDown={true}
      isOpen={isMenuOpen}
      menu={
        <SearchableMenu
          width={MenuWidth.MEDIUM}
          height={MenuHeight.MEDIUM}
          onSelect={handleSelectKey}
          optionDatas={fieldKeyOptionDatas}
          selectedValue={null}
          getMatchingOptionDatas={(query: string, optionDatas: Array<OptionData>) =>
            getMatchingOptionDatas(query, optionDatas)
          }
        />
      }
      onToggle={(isOpen) => {
        setIsMenuOpen(isOpen);
      }}
    >
      <AddIcon
        className={css(styles.addIcon)}
        onClick={() => {
          setIsMenuOpen(true);
        }}
      />
    </Dropdown>
  );
}

interface CustomFieldItemProps {
  fieldName: string;
  fieldValue: string;
  onUpdateCustomField: (newField: CustomField) => void;
  onRemoveCustomField: () => void;
  isReadOnly: boolean;
}

function CustomFieldItem({
  fieldName,
  fieldValue,
  onUpdateCustomField,
  onRemoveCustomField,
  isReadOnly,
}: CustomFieldItemProps): JSX.Element {
  return (
    <FormGroup className={css(styles.fieldRow)}>
      <FieldLabel className={css(styles.fieldText)} label={fieldName} />
      {isReadOnly ? (
        <p className={css(styles.fieldText)}>{fieldValue}</p>
      ) : (
        <div className={css(styles.fieldValueContainer)}>
          <TextInput
            value={fieldValue}
            onChange={(e) => {
              onUpdateCustomField({name: fieldName, value: e.target.value});
            }}
            placeholder="Enter value"
          />
          <BenchlingIcon
            className={css(styles.deleteIcon)}
            icon="close"
            onClick={() => {
              onRemoveCustomField();
            }}
          />
        </div>
      )}
    </FormGroup>
  );
}

interface CustomFieldListProps {
  customFields: Array<CustomField>;
  onUpdateCustomField: (index: number, newField: CustomField) => void;
  onRemoveCustomField: (idxToRemove: number) => void;
  emptyFieldsMessage: string;
  isReadOnly: boolean;
}

function CustomFieldList({
  customFields,
  onUpdateCustomField,
  onRemoveCustomField,
  emptyFieldsMessage,
  isReadOnly,
}: CustomFieldListProps): JSX.Element {
  if (customFields.length === 0) {
    return <div className={css(styles.emptyListRow)}>{emptyFieldsMessage}</div>;
  }
  return (
    <div className={css(styles.fieldsContainer)}>
      {customFields.map(({name, value}, idx) => (
        <CustomFieldItem
          key={idx}
          fieldName={name}
          fieldValue={value}
          onUpdateCustomField={(newField) => {
            onUpdateCustomField(idx, newField);
          }}
          onRemoveCustomField={() => {
            onRemoveCustomField(idx);
          }}
          isReadOnly={isReadOnly}
        />
      ))}
    </div>
  );
}

export interface CustomFieldsFormProps {
  className?: string;
  customFields: Array<CustomField>;
  setCustomFields: (newFields: Array<CustomField>) => void;
  allCustomFieldKeys: Array<string>;
  emptyFieldsMessage: string;
  isReadOnly: boolean;
}

/**
 * Component for modifying custom fields on a sequence feature
 */
function CustomFieldsForm({
  className,
  customFields,
  setCustomFields,
  allCustomFieldKeys,
  emptyFieldsMessage,
  isReadOnly,
}: CustomFieldsFormProps): JSX.Element {
  function onRemoveCustomField(idxToRemove: number): void {
    setCustomFields(customFields.filter((_, index) => index !== idxToRemove));
  }

  function onUpdateCustomField(index: number, newField: CustomField): void {
    setCustomFields([...customFields.slice(0, index), newField, ...customFields.slice(index + 1)]);
  }

  function onAddCustomField(key: string): void {
    setCustomFields([...customFields, {name: key, value: ''}]);
  }

  return (
    <div className={classNames(css(styles.formContainer), className)}>
      <div className={css(styles.formHeader)}>
        <div>Custom fields</div>
        {!isReadOnly && (
          <AddCustomFieldMenu onAddField={onAddCustomField} allCustomFieldKeyOptions={allCustomFieldKeys} />
        )}
      </div>
      <CustomFieldList
        customFields={customFields}
        onUpdateCustomField={onUpdateCustomField}
        onRemoveCustomField={onRemoveCustomField}
        emptyFieldsMessage={emptyFieldsMessage}
        isReadOnly={isReadOnly}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  formHeader: {
    ...FONT_PROXIMA_TITLE,
    display: 'flex',
    backgroundColor: GRAY_LIGHTER,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    padding: '8px',
  },
  addIcon: {
    marginTop: '2px',
    marginLeft: '8px',
  },
  emptyListRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
  },
  fieldsContainer: {
    padding: '4px 8px',
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '8px 0',
  },
  fieldText: {
    marginBottom: '0',
    marginTop: '4px',
  },
  fieldValueContainer: {
    display: 'flex',
  },
  deleteIcon: {
    marginLeft: '12px',
    marginTop: '8px',
    width: '12px',
    height: '12px',
    color: GRAY_DARKER,
    cursor: 'pointer',
  },
  createNewOption: {
    color: BLUE,
    marginBottom: '0',
  },
});

export default CustomFieldsForm;
